// src/components/onboarding/steps/SourceSelectionStep.tsx

import { useMemo, useState } from "react";
import { Badge, Box, Card, Stack, Text } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";

import { ConnectorIcon } from "@/components/data-sources/ConnectorIcon";
import {
  CatalogSearchInput,
  CatalogTileGroups,
  type CatalogTileBase,
} from "@/components/data-sources/connectorCatalog/CatalogTileGroups";
import { ConnectorRequestModal } from "@/components/data-sources/connectorCatalog/ConnectorRequestModal";
import {
  groupForConnector,
  type CatalogGroupKey,
} from "@/components/data-sources/connectorCatalog/catalogGroups";
import {
  isActivatable,
  requestTileLabel,
  tileStateOf,
} from "@/components/data-sources/connectorCatalog/tileState";
import { useCanRequestConnectors } from "@/components/data-sources/connectorCatalog/useCanRequestConnectors";
import { useConnectorCatalog } from "@/components/data-sources/connectorCatalog/useConnectorCatalog";
import { useAllowedSourceTypes } from "@/hooks/useAllowedSourceTypes";
import type { ConnectorTileState, ManagedSourceConnector } from "@/types/managedSources";

export type ConnectorOption = {
  connector_type: string;
  /**
   * Canonical key from SOURCE_TYPE_REGISTRY (registry-mapping.ts), which is
   * what the per-account allowlist stores. Written out per entry on purpose:
   * the backend's CONNECTOR_TO_REGISTRY_KEY covers managed connectors only and
   * has no entry for CUSTOM_BIGQUERY or CUSTOM_SQL, so resolving through it
   * would hide BigQuery and SQL from every restricted account, including
   * accounts whose allowlist explicitly contains "other-bq". A typo here fails
   * the registry test in __tests__/SourceSelectionStep.test.tsx rather than
   * silently dropping a card.
   */
  registryKey: string;
  display_name: string;
  description: string;
  /** Picker group: a direct-connect option has no connector to take it from. */
  group: CatalogGroupKey;
};

/**
 * The direct-connect options: they have no managed connector, so the
 * connectors endpoint does not list them and the session allowlist decides.
 * Every managed connector comes from the endpoint with its tile state
 * (ADR-0410).
 */
export const DIRECT_CONNECT_OPTIONS: ConnectorOption[] = [
  {
    connector_type: "CUSTOM_BIGQUERY",
    registryKey: "other-bq",
    display_name: "Custom BigQuery",
    description: "Connect an existing BigQuery dataset",
    group: "warehouses",
  },
  {
    connector_type: "CUSTOM_SQL",
    registryKey: "other-sql",
    display_name: "Custom SQL",
    description: "Connect via SQL database",
    group: "warehouses",
  },
];

type OnboardingTile = CatalogTileBase & {
  connectorType: string;
  state: ConnectorTileState;
  requestedAt?: string | null;
  connector?: ManagedSourceConnector;
};

type Props = {
  selected: Set<string>;
  /** `displayName` is what the source to-do list shows for this type. */
  onToggle: (connectorType: string, displayName: string) => void;
};

export default function SourceSelectionStep({ selected, onToggle }: Props) {
  const { isAllowed } = useAllowedSourceTypes();
  const catalog = useConnectorCatalog(true);
  const canRequest = useCanRequestConnectors();
  const [query, setQuery] = useState("");
  const [requesting, setRequesting] = useState<ManagedSourceConnector | null>(null);

  const tiles = useMemo<OnboardingTile[]>(() => {
    const managed: OnboardingTile[] = catalog.connectors.map((c) => ({
      key: c.connector_type,
      connectorType: c.connector_type,
      label: c.label,
      subtitle: c.description,
      group: groupForConnector(c),
      state: tileStateOf(c),
      requestedAt: c.requested_at ?? null,
      connector: c,
    }));
    // Same filter as the "Add data source" modal, for the same reason:
    // offering a type the account may not activate walks the customer through
    // a wizard the backend guard refuses at the end. Purely UX.
    const direct: OnboardingTile[] = DIRECT_CONNECT_OPTIONS.filter((o) =>
      isAllowed(o.registryKey),
    ).map((o) => ({
      key: o.connector_type,
      connectorType: o.connector_type,
      label: o.display_name,
      subtitle: o.description,
      group: o.group,
      state: "available" as const,
    }));
    return [...managed, ...direct];
  }, [catalog.connectors, isAllowed]);

  function handleClick(tile: OnboardingTile) {
    if (isActivatable(tile.state)) {
      onToggle(tile.connectorType, tile.label);
      return;
    }
    if (tile.state === "on_request" && tile.connector) setRequesting(tile.connector);
  }

  const nothingToShow = !catalog.loading && !catalog.error && tiles.length === 0;

  return (
    <Stack gap="lg">
      <Box>
        <Text size="xl" fw={700}>
          3 — Select your sources
        </Text>
        <Text size="sm" c="dimmed" mt={4}>
          We'll save your selection. You can connect these sources later from Data Management.
        </Text>
      </Box>

      {nothingToShow ? (
        <Stack gap="xs" py="lg" align="center">
          <Text fw={600}>No data source types are enabled for your account.</Text>
          <Text size="sm" c="dimmed">
            Contact your account administrator to request access.
          </Text>
        </Stack>
      ) : (
        <>
          <CatalogSearchInput value={query} onChange={setQuery} />
          <CatalogTileGroups
            tiles={tiles}
            query={query}
            loading={catalog.loading}
            error={catalog.error}
            onRetry={catalog.reload}
            cols={{ base: 2, sm: 3 }}
            renderTile={(tile) => {
              const isSelected = selected.has(tile.connectorType);
              const badge = requestTileLabel(tile.state, tile.requestedAt);
              const isRequested = tile.state === "requested";
              return (
                <Card
                  padding="md"
                  radius="md"
                  withBorder
                  role="button"
                  tabIndex={isRequested ? -1 : 0}
                  aria-pressed={isActivatable(tile.state) ? isSelected : undefined}
                  aria-disabled={isRequested || undefined}
                  aria-label={badge ? `${tile.label}, ${badge}` : tile.label}
                  data-tile-state={tile.state}
                  onClick={() => handleClick(tile)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleClick(tile);
                    }
                  }}
                  style={{
                    cursor: isRequested ? "default" : "pointer",
                    borderColor: isSelected ? "var(--mantine-color-blue-5)" : undefined,
                    borderWidth: isSelected ? 2 : 1,
                    position: "relative",
                    transition: "all 150ms ease",
                  }}
                  className="onboarding-card"
                >
                  {isSelected && (
                    <Box
                      style={{
                        position: "absolute",
                        top: 8,
                        right: 8,
                        width: 22,
                        height: 22,
                        borderRadius: "50%",
                        backgroundColor: "var(--mantine-color-blue-5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <IconCheck size={14} color="white" />
                    </Box>
                  )}

                  <Stack gap={8} align="center">
                    <Box style={{ opacity: isRequested ? 0.5 : 1 }}>
                      <ConnectorIcon type={tile.connectorType} size={28} />
                    </Box>
                    <Text size="sm" fw={600} ta="center">
                      {tile.label}
                    </Text>
                    {badge && (
                      <Badge
                        size="xs"
                        variant="light"
                        color={isRequested ? "green" : "gray"}
                        style={{ textTransform: "none" }}
                      >
                        {badge}
                      </Badge>
                    )}
                    <Text size="xs" c="dimmed" ta="center" lineClamp={2}>
                      {tile.subtitle}
                    </Text>
                  </Stack>
                </Card>
              );
            }}
          />
        </>
      )}

      <ConnectorRequestModal
        connector={requesting}
        canRequest={canRequest}
        onClose={() => setRequesting(null)}
        onRequested={catalog.markRequested}
      />
    </Stack>
  );
}
